/**
 * Visibility Tracking Composable
 *
 * Provides IntersectionObserver-based visibility detection for accurate view tracking.
 * Tracks when elements become visible in the viewport and triggers callbacks.
 *
 * IMPORTANT: Must be used inside setup() or components only.
 *
 * Usage:
 * ```typescript
 * const { elementRef, isVisible, hasBeenViewed } = useVisibilityTracking({
 *   threshold: 0.5,
 *   onFirstView: () => {
 *     trackComponentView({ component: 'MyComponent' })
 *   },
 *   onVisibilityChange: (isVisible) => {
 *     console.log('Component visibility changed:', isVisible)
 *   },
 * })
 *
 * // In template:
 * <div ref="elementRef">Content</div>
 * ```
 */

import { ref, onMounted, onUnmounted, Ref } from 'vue'

/**
 * Configuration options for visibility tracking
 */
export interface VisibilityTrackingOptions {
  /**
   * Intersection threshold (0-1 or array of thresholds)
   * 0.5 = 50% visible (default, industry standard)
   * 0.1 = 10% visible (lenient)
   * 1.0 = 100% visible (strict)
   */
  threshold?: number | number[]

  /**
   * Root margin for intersection detection
   * Format: "top right bottom left" (like CSS margin)
   * Example: "100px 0px 100px 0px" = start tracking 100px before entering viewport
   */
  rootMargin?: string

  /**
   * Callback when element first becomes visible
   * Called only once per element
   */
  onFirstView?: () => void

  /**
   * Callback when visibility changes
   * Called every time visibility state changes
   */
  onVisibilityChange?: (isVisible: boolean) => void

  /**
   * Callback when element becomes fully visible
   * Called when 100% of element is visible
   */
  onFullyVisible?: () => void

  /**
   * Root element for intersection (null = viewport)
   */
  root?: Element | null

  /**
   * Optional element ref to use instead of creating a new one
   * Useful when integrating with other composables
   */
  elementRef?: Ref<HTMLElement | null>

  /**
   * Enable debug logging
   */
  debug?: boolean
}

/**
 * Visibility tracking state
 */
export interface VisibilityTrackingState {
  elementRef: Ref<HTMLElement | null>
  isVisible: Ref<boolean>
  hasBeenViewed: Ref<boolean>
  visibilityRatio: Ref<number>
  disconnect: () => void
}

/**
 * Track element visibility using IntersectionObserver
 * MUST be called inside setup() or components only
 *
 * DEFENSIVE: Never breaks component functionality if tracking fails
 */
export function useVisibilityTracking(
  options: VisibilityTrackingOptions = {}
): VisibilityTrackingState {
  // Use provided elementRef or create a new one
  const elementRef = options.elementRef ?? ref<HTMLElement | null>(null)
  const isVisible = ref(false)
  const hasBeenViewed = ref(false)
  const visibilityRatio = ref(0)
  let observer: IntersectionObserver | null = null

  // Normalize options
  const threshold = options.threshold ?? 0.5
  const rootMargin = options.rootMargin ?? '0px'
  const debug = options.debug ?? false

  /**
   * Handle intersection changes
   */
  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    try {
      for (const entry of entries) {
        const wasVisible = isVisible.value
        isVisible.value = entry.isIntersecting
        visibilityRatio.value = entry.intersectionRatio

        if (debug) {
          console.log(
            `[Visibility] ${entry.target.tagName}: isIntersecting=${entry.isIntersecting}, ratio=${entry.intersectionRatio.toFixed(2)}`
          )
        }

        // Track first view (at any visibility threshold)
        if (entry.isIntersecting && !hasBeenViewed.value) {
          hasBeenViewed.value = true
          if (debug) console.log('[Visibility] First view detected')
          options.onFirstView?.()
        }

        // Track fully visible (100% visible, separate from first view)
        if (entry.intersectionRatio === 1) {
          if (debug) console.log('[Visibility] Fully visible detected')
          options.onFullyVisible?.()
        }

        // Track visibility changes
        if (wasVisible !== entry.isIntersecting) {
          if (debug) console.log(`[Visibility] Visibility changed to ${entry.isIntersecting}`)
          options.onVisibilityChange?.(entry.isIntersecting)
        }
      }
    } catch (error) {
      console.warn('[Visibility] Error in intersection handler:', error)
      // Continue tracking even if callback fails
    }
  }

  /**
   * Initialize observer
   */
  const initializeObserver = (): void => {
    try {
      if (!elementRef.value) {
        // Element ref not set yet - this is normal when using with useComponentTracking
        // The ref will be set when the component template renders
        // Retry initialization on next tick
        if (debug) console.log('[Visibility] Element ref not set yet, will retry')

        // Schedule retry for next tick
        setTimeout(() => {
          if (elementRef.value && !observer) {
            initializeObserver()
          }
        }, 50)
        return
      }

      // Create observer with options
      observer = new IntersectionObserver(handleIntersection, {
        threshold,
        rootMargin,
        root: options.root ?? null,
      })

      // Start observing
      observer.observe(elementRef.value)

      if (debug) console.log('[Visibility] Observer initialized and observing element')
    } catch (error) {
      console.warn('[Visibility] Failed to initialize observer:', error)
      // Continue without observer
    }
  }

  /**
   * Disconnect observer
   */
  const disconnect = (): void => {
    try {
      if (observer) {
        observer.disconnect()
        observer = null
        if (debug) console.log('[Visibility] Observer disconnected')
      }
    } catch (error) {
      console.warn('[Visibility] Error disconnecting observer:', error)
    }
  }

  /**
   * Setup observer on mount
   */
  onMounted(() => {
    // Use nextTick to ensure element is rendered
    setTimeout(() => {
      initializeObserver()
    }, 0)
  })

  /**
   * Cleanup on unmount
   */
  onUnmounted(() => {
    disconnect()
  })

  return {
    elementRef,
    isVisible,
    hasBeenViewed,
    visibilityRatio,
    disconnect,
  }
}

/**
 * Track multiple elements with visibility detection
 * Useful for tracking multiple components on same page
 *
 * Usage:
 * ```typescript
 * const { track, disconnect } = useMultipleVisibilityTracking()
 *
 * track(element1, {
 *   onFirstView: () => trackView('component1')
 * })
 *
 * track(element2, {
 *   onFirstView: () => trackView('component2')
 * })
 * ```
 */
export function useMultipleVisibilityTracking(
  globalOptions: VisibilityTrackingOptions = {}
) {
  const trackedElements = new Map<HTMLElement, VisibilityTrackingState>()

  /**
   * Track an element
   */
  const track = (element: HTMLElement, options: VisibilityTrackingOptions = {}): void => {
    try {
      if (trackedElements.has(element)) {
        if (globalOptions.debug) console.warn('[Visibility] Element already tracked')
        return
      }

      const mergedOptions = { ...globalOptions, ...options }
      const state = useVisibilityTracking(mergedOptions)
      state.elementRef.value = element

      trackedElements.set(element, state)

      if (globalOptions.debug) console.log('[Visibility] Element tracked')
    } catch (error) {
      console.warn('[Visibility] Error tracking element:', error)
    }
  }

  /**
   * Untrack an element
   */
  const untrack = (element: HTMLElement): void => {
    try {
      const state = trackedElements.get(element)
      if (state) {
        state.disconnect()
        trackedElements.delete(element)
        if (globalOptions.debug) console.log('[Visibility] Element untracked')
      }
    } catch (error) {
      console.warn('[Visibility] Error untracking element:', error)
    }
  }

  /**
   * Disconnect all observers
   */
  const disconnect = (): void => {
    try {
      for (const [, state] of trackedElements) {
        state.disconnect()
      }
      trackedElements.clear()
      if (globalOptions.debug) console.log('[Visibility] All observers disconnected')
    } catch (error) {
      console.warn('[Visibility] Error disconnecting all observers:', error)
    }
  }

  /**
   * Get tracking state for element
   */
  const getState = (element: HTMLElement): VisibilityTrackingState | undefined => {
    return trackedElements.get(element)
  }

  /**
   * Get all tracked elements
   */
  const getTrackedElements = (): HTMLElement[] => {
    return Array.from(trackedElements.keys())
  }

  /**
   * Cleanup on unmount
   */
  onUnmounted(() => {
    disconnect()
  })

  return {
    track,
    untrack,
    disconnect,
    getState,
    getTrackedElements,
  }
}
